import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Bug, BookOpen, Search, Plus, Trash2, Pencil, X, Loader2, Printer, Download, ShieldAlert, ArrowLeft } from 'lucide-react';
import { apiPestLibrary, apiKnowledge } from '../../lib/api';
import { useConfig } from '../../context/ConfigContext';
import { printHTML, toCSV, downloadFile } from '../../lib/importExport';
import { toast } from 'sonner';

const GREEN = '#8dd110';
const PEST_CATEGORIES = ['Cockroaches', 'Rodents', 'Ants', 'Spiders', 'Termites', 'Bed Bugs', 'Fleas & Ticks', 'Stinging Insects', 'Flies', 'Occasional Invaders', 'Wildlife', 'Other'];
const KB_CATEGORIES = ['Approach', 'Service Prep', 'Prevention', 'Safety', 'Policies', 'Reference', 'Other'];
const SEVERITIES = ['Low', 'Medium', 'High'];

const sevBadge = (s) =>
  s === 'High' ? 'bg-red-100 text-red-700'
  : s === 'Medium' ? 'bg-amber-100 text-amber-700'
  : 'bg-emerald-100 text-emerald-700';

function useRole() {
  try { return (JSON.parse(localStorage.getItem('pp_user') || '{}').role) || 'admin'; }
  catch { return 'admin'; }
}

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// --------------------------------------------------------------- Pest edit modal
function PestModal({ pest, onClose, onSaved }) {
  const empty = { name: '', scientificName: '', category: 'Other', severity: 'Medium', emoji: '\u{1F41B}', description: '', identification: '', biology: '', signs: '', healthRisks: '', treatment: '', prevention: '', seasonality: '' };
  const [f, setF] = useState(pest ? { ...empty, ...pest } : empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name.trim()) { toast.error('Pest name is required'); return; }
    setSaving(true);
    try {
      if (pest?.id) await apiPestLibrary.update(pest.id, f);
      else await apiPestLibrary.create(f);
      toast.success(pest ? 'Pest updated' : 'Pest added');
      onSaved(); onClose();
    } catch { toast.error('Could not save. Admin access is required to edit the library.'); }
    finally { setSaving(false); }
  };
  const Field = ({ k, label, area }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      {area
        ? <textarea value={f[k]} onChange={set(k)} rows={2} className="mt-1 w-full text-sm border border-slate-200 rounded-md p-2" />
        : <Input value={f[k]} onChange={set(k)} className="mt-1 h-9" />}
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-bold text-slate-900">{pest ? 'Edit Pest' : 'Add Pest'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field k="name" label="Common name" />
            <Field k="scientificName" label="Scientific name" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Category</Label><Select value={f.category} onValueChange={v => setF({ ...f, category: v })}><SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger><SelectContent>{PEST_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Severity</Label><Select value={f.severity} onValueChange={v => setF({ ...f, severity: v })}><SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger><SelectContent>{SEVERITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <Field k="emoji" label="Icon (emoji)" />
          </div>
          <Field k="description" label="Description" area />
          <Field k="identification" label="Identification" area />
          <Field k="biology" label="Biology / behavior" area />
          <Field k="signs" label="Signs of infestation" area />
          <Field k="healthRisks" label="Health risks" area />
          <Field k="treatment" label="Treatment (our approach)" area />
          <Field k="prevention" label="Prevention" area />
          <Field k="seasonality" label="Seasonality" />
          <Button onClick={save} disabled={saving} className="w-full text-white" style={{ backgroundColor: GREEN }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (pest ? 'Save Changes' : 'Add Pest')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- Pest detail
function PestDetail({ pest, contact, onBack, onEdit, canEdit }) {
  const rows = [
    ['Identification', pest.identification],
    ['Biology & Behavior', pest.biology],
    ['Signs of Infestation', pest.signs],
    ['Health Risks', pest.healthRisks],
    ['Our Treatment Approach', pest.treatment],
    ['Prevention', pest.prevention],
    ['Seasonality', pest.seasonality],
  ].filter(([, v]) => v);

  const print = () => {
    const c = contact || {};
    printHTML(`
      <div style="border-bottom:3px solid ${GREEN};padding-bottom:10px;margin-bottom:14px">
        <div class="brand" style="font-size:20px">${esc(c.companyName || 'Xpertpests Solutions')}</div>
        <div style="font-size:12px;color:#475569">${esc(c.phone1 || '')} ${c.phone2 ? '&bull; ' + esc(c.phone2) : ''} &bull; ${esc(c.email || '')}</div>
      </div>
      <h1 style="margin:0">${esc(pest.emoji || '')} ${esc(pest.name)}</h1>
      <div style="font-style:italic;color:#64748b;margin-bottom:6px">${esc(pest.scientificName || '')}</div>
      <div style="margin-bottom:12px">
        <span style="background:#f1f5f9;border-radius:6px;padding:2px 8px;font-size:12px;margin-right:6px">${esc(pest.category)}</span>
        <span style="background:#f1f5f9;border-radius:6px;padding:2px 8px;font-size:12px">Severity: ${esc(pest.severity)}</span>
      </div>
      <p>${esc(pest.description)}</p>
      ${rows.map(([h, v]) => `<h3 style="margin:14px 0 4px">${esc(h)}</h3><p style="margin:0;white-space:pre-line">${esc(v)}</p>`).join('')}
      <p style="margin-top:24px;font-size:11px;color:#94a3b8">Reference guide prepared by ${esc(c.companyName || 'Xpertpests')}. ${esc(c.tagline || '')}</p>
    `);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={onBack} className="gap-1.5"><ArrowLeft className="w-4 h-4" />Back to library</Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={print} className="gap-1.5"><Printer className="w-4 h-4" />Print / PDF</Button>
          {canEdit && <Button size="sm" onClick={onEdit} className="gap-1.5 text-white" style={{ backgroundColor: GREEN }}><Pencil className="w-4 h-4" />Edit</Button>}
        </div>
      </div>
      <Card className="p-6 border-slate-200">
        <div className="flex items-start gap-4">
          <div className="text-5xl leading-none">{pest.emoji || '\u{1F41B}'}</div>
          <div className="min-w-0">
            <h1 className="text-2xl font-extrabold text-slate-900">{pest.name}</h1>
            <div className="italic text-slate-500">{pest.scientificName}</div>
            <div className="flex gap-2 mt-2 flex-wrap">
              <Badge className="bg-slate-100 text-slate-700 border-0">{pest.category}</Badge>
              <Badge className={`border-0 ${sevBadge(pest.severity)}`}>Severity: {pest.severity}</Badge>
              {(pest.aka || []).map(a => <Badge key={a} variant="outline" className="text-slate-500">aka {a}</Badge>)}
            </div>
          </div>
        </div>
        <p className="mt-4 text-slate-700 leading-relaxed">{pest.description}</p>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {rows.map(([h, v]) => (
            <div key={h} className={h === 'Health Risks' ? 'md:col-span-2' : ''}>
              <div className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: h === 'Health Risks' ? '#dc2626' : GREEN }}>
                {h === 'Health Risks' && <ShieldAlert className="w-3.5 h-3.5" />}{h}
              </div>
              <p className="text-sm text-slate-700 mt-1 whitespace-pre-line leading-relaxed">{v}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// --------------------------------------------------------------- KB modal
function KbModal({ article, onClose, onSaved }) {
  const empty = { title: '', category: 'Reference', audience: 'All', summary: '', body: '' };
  const [f, setF] = useState(article ? { ...empty, ...article } : empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (article?.id) await apiKnowledge.update(article.id, f);
      else await apiKnowledge.create(f);
      toast.success(article ? 'Article updated' : 'Article added');
      onSaved(); onClose();
    } catch { toast.error('Could not save. Admin access is required.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-bold text-slate-900">{article ? 'Edit Article' : 'Add Article'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><Label className="text-xs">Title</Label><Input value={f.title} onChange={set('title')} className="mt-1 h-9" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Category</Label><Select value={f.category} onValueChange={v => setF({ ...f, category: v })}><SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger><SelectContent>{KB_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Audience</Label><Select value={f.audience} onValueChange={v => setF({ ...f, audience: v })}><SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger><SelectContent>{['All', 'Customer', 'Staff'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label className="text-xs">Summary</Label><textarea value={f.summary} onChange={set('summary')} rows={2} className="mt-1 w-full text-sm border border-slate-200 rounded-md p-2" /></div>
          <div><Label className="text-xs">Body</Label><textarea value={f.body} onChange={set('body')} rows={10} className="mt-1 w-full text-sm border border-slate-200 rounded-md p-2 font-mono" /></div>
          <Button onClick={save} disabled={saving} className="w-full text-white" style={{ backgroundColor: GREEN }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (article ? 'Save Changes' : 'Add Article')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- Main page
export default function PestLibrary() {
  const role = useRole();
  const canEdit = role === 'admin';
  const cfg = useConfig();
  const contact = cfg?.contact;

  const [tab, setTab] = useState('pests');
  const [pests, setPests] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [selected, setSelected] = useState(null);      // pest detail
  const [editPest, setEditPest] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [openArticle, setOpenArticle] = useState(null);
  const [editArticle, setEditArticle] = useState(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([apiPestLibrary.list().catch(() => []), apiKnowledge.list().catch(() => [])]);
      setPests(Array.isArray(p) ? p : []);
      setArticles(Array.isArray(a) ? a : []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cats = useMemo(() => ['all', ...Array.from(new Set(pests.map(p => p.category).filter(Boolean)))], [pests]);
  const filteredPests = useMemo(() => {
    const s = q.trim().toLowerCase();
    return pests.filter(p =>
      (cat === 'all' || p.category === cat) &&
      (!s || [p.name, p.scientificName, p.category, p.description, ...(p.aka || [])].join(' ').toLowerCase().includes(s))
    );
  }, [pests, q, cat]);
  const filteredArticles = useMemo(() => {
    const s = q.trim().toLowerCase();
    return articles.filter(a => !s || [a.title, a.category, a.summary, a.body].join(' ').toLowerCase().includes(s));
  }, [articles, q]);

  const delPest = async (id) => { if (!window.confirm('Remove this pest from the library?')) return; try { await apiPestLibrary.remove(id); toast.success('Removed'); setSelected(null); load(); } catch { toast.error('Could not remove'); } };
  const delArticle = async (id) => { if (!window.confirm('Remove this article?')) return; try { await apiKnowledge.remove(id); toast.success('Removed'); setOpenArticle(null); load(); } catch { toast.error('Could not remove'); } };

  const exportCsv = () => {
    const cols = [
      { key: 'name', header: 'Name' }, { key: 'scientificName', header: 'Scientific Name' },
      { key: 'category', header: 'Category' }, { key: 'severity', header: 'Severity' },
      { key: 'description', header: 'Description' }, { key: 'signs', header: 'Signs' },
      { key: 'treatment', header: 'Treatment' }, { key: 'prevention', header: 'Prevention' },
    ];
    downloadFile('pest-library.csv', toCSV(pests, cols));
  };

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div className="flex flex-wrap justify-between gap-3 items-center">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Bug className="w-7 h-7" style={{ color: GREEN }} />Pest Library &amp; Knowledge Base</h1>
          <p className="text-slate-500 mt-1">Identification, biology, treatment, and prep guides — a field reference for your team and customers.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} className="gap-1.5"><Download className="w-4 h-4" />Export CSV</Button>
          {canEdit && tab === 'pests' && <Button onClick={() => setEditPest(null)} className="gap-1.5 text-white" style={{ backgroundColor: GREEN }}><Plus className="w-4 h-4" />Add Pest</Button>}
          {canEdit && tab === 'kb' && <Button onClick={() => setEditArticle(null)} className="gap-1.5 text-white" style={{ backgroundColor: GREEN }}><Plus className="w-4 h-4" />Add Article</Button>}
        </div>
      </div>

      <div className="flex gap-2">
        {[['pests', 'Pest Library', Bug], ['kb', 'Knowledge Base', BookOpen]].map(([t, label, Ico]) => (
          <button key={t} onClick={() => { setTab(t); setSelected(null); setOpenArticle(null); }}
            className={`px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-1.5 transition ${tab === t ? 'text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            style={tab === t ? { backgroundColor: GREEN } : {}}>
            <Ico className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {!selected && !openArticle && (
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder={tab === 'pests' ? 'Search pests, symptoms, biology…' : 'Search articles…'} className="pl-9 h-10 bg-white" />
          </div>
          {tab === 'pests' && (
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="h-10 w-52"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map(c => <SelectItem key={c} value={c}>{c === 'all' ? 'All categories' : c}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : tab === 'pests' ? (
        selected ? (
          <PestDetail pest={selected} contact={contact} canEdit={canEdit}
            onBack={() => setSelected(null)} onEdit={() => setEditPest(selected)} />
        ) : filteredPests.length === 0 ? (
          <Card className="p-12 text-center text-slate-400 border-slate-200">No pests match your search.</Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredPests.map(p => (
              <Card key={p.id} onClick={() => setSelected(p)} className="p-4 border-slate-200 hover:shadow-md hover:border-slate-300 cursor-pointer transition group">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-3xl leading-none">{p.emoji || '\u{1F41B}'}</div>
                  <div className="flex items-center gap-1">
                    <Badge className={`border-0 text-[10px] ${sevBadge(p.severity)}`}>{p.severity}</Badge>
                    {canEdit && (
                      <button onClick={e => { e.stopPropagation(); setEditPest(p); }} className="text-slate-300 hover:text-slate-600 opacity-0 group-hover:opacity-100"><Pencil className="w-3.5 h-3.5" /></button>
                    )}
                    {canEdit && (
                      <button onClick={e => { e.stopPropagation(); delPest(p.id); }} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </div>
                <div className="font-bold text-slate-900 mt-2">{p.name}</div>
                <div className="text-xs italic text-slate-400">{p.scientificName}</div>
                <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{p.description}</p>
                <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px] mt-2">{p.category}</Badge>
              </Card>
            ))}
          </div>
        )
      ) : (
        // Knowledge base
        openArticle ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => setOpenArticle(null)} className="gap-1.5"><ArrowLeft className="w-4 h-4" />Back</Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => printHTML(`<h1>${esc(openArticle.title)}</h1><p style="white-space:pre-line">${esc(openArticle.body)}</p>`)} className="gap-1.5"><Printer className="w-4 h-4" />Print / PDF</Button>
                {canEdit && <Button size="sm" onClick={() => setEditArticle(openArticle)} className="gap-1.5 text-white" style={{ backgroundColor: GREEN }}><Pencil className="w-4 h-4" />Edit</Button>}
                {canEdit && <Button size="sm" variant="outline" onClick={() => delArticle(openArticle.id)} className="gap-1.5 text-red-600"><Trash2 className="w-4 h-4" /></Button>}
              </div>
            </div>
            <Card className="p-6 border-slate-200">
              <div className="flex gap-2 flex-wrap mb-2">
                <Badge className="bg-slate-100 text-slate-700 border-0">{openArticle.category}</Badge>
                {openArticle.audience && <Badge variant="outline" className="text-slate-500">{openArticle.audience}</Badge>}
              </div>
              <h1 className="text-2xl font-extrabold text-slate-900">{openArticle.title}</h1>
              <p className="mt-3 text-slate-700 whitespace-pre-line leading-relaxed">{openArticle.body}</p>
            </Card>
          </div>
        ) : filteredArticles.length === 0 ? (
          <Card className="p-12 text-center text-slate-400 border-slate-200">No articles match your search.</Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {filteredArticles.map(a => (
              <Card key={a.id} onClick={() => setOpenArticle(a)} className="p-5 border-slate-200 hover:shadow-md hover:border-slate-300 cursor-pointer transition group">
                <div className="flex items-start justify-between gap-2">
                  <BookOpen className="w-5 h-5 flex-shrink-0" style={{ color: GREEN }} />
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <button onClick={e => { e.stopPropagation(); setEditArticle(a); }} className="text-slate-300 hover:text-slate-600 opacity-0 group-hover:opacity-100"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={e => { e.stopPropagation(); delArticle(a.id); }} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
                <div className="font-bold text-slate-900 mt-2">{a.title}</div>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{a.summary}</p>
                <div className="flex gap-2 mt-3">
                  <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">{a.category}</Badge>
                  {a.audience && <Badge variant="outline" className="text-slate-400 text-[10px]">{a.audience}</Badge>}
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {editPest !== undefined && <PestModal pest={editPest} onClose={() => setEditPest(undefined)} onSaved={() => { load(); if (selected) setSelected(null); }} />}
      {editArticle !== undefined && <KbModal article={editArticle} onClose={() => setEditArticle(undefined)} onSaved={() => { load(); if (openArticle) setOpenArticle(null); }} />}
    </div>
  );
}
